/**
 * usePollWhileProcessing — runs a CompletionPoller while `active` is true.
 *
 * A best-effort WebSocket push is the fast path for async-job completion (POC,
 * meds), but it can be missed. This hook is the reliable fallback: while a
 * screen is in its in-progress state, it polls the server status endpoint and
 * lets the caller converge to the real state. Stops automatically when `active`
 * goes false or the component unmounts. See
 * docs/audits/2026-06-05-poc-stuck-websocket-zombie-rca.md.
 */

import { useEffect, useRef } from "react";

import { CompletionPoller, type CompletionPollerConfig } from "@/lib/completion-poller";

export interface UsePollWhileProcessingOptions<T> extends CompletionPollerConfig<T> {
    /** Poll only while this is true (e.g. `phase === "generating"`). */
    active: boolean;
}

export function usePollWhileProcessing<T>({
    active,
    ...config
}: UsePollWhileProcessingOptions<T>): void {
    // Latest config in a ref so the effect doesn't re-create the poller on every
    // render (poll/onResult identities change each render). Only `active`
    // start/stops the poller.
    const configRef = useRef(config);
    configRef.current = config;

    useEffect(() => {
        if (!active) {
            return;
        }
        const poller = new CompletionPoller<T>({
            intervalMs: configRef.current.intervalMs,
            timeoutMs: configRef.current.timeoutMs,
            poll: () => configRef.current.poll(),
            isTerminal: (r) => configRef.current.isTerminal(r),
            onResult: (r) => configRef.current.onResult(r),
            onTimeout: () => configRef.current.onTimeout(),
            onError: (e) => configRef.current.onError?.(e)
        });
        poller.start();
        return () => poller.stop();
    }, [active]);
}
